import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker, useLocation, useNavigate, useParams } from 'react-router';
import { observer } from 'mobx-react-lite';
import { Button, Loading } from '@linktivity/link-ui';

import { useStore } from '@link/shinkansen/stores';
import { OrderStatus } from '@link/shinkansen/services/types';

import cardStyles from '../Confirm/confirm.module.css';
import styles from './submitted.module.css';

const SubmittedView = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  useBlocker(
    ({ historyAction }) =>
      location.state?.submittedOrder === true && historyAction === 'POP'
  );
  const { token, bookingId: routeBookingId } = useParams();
  const { shinkansen } = useStore();
  const { bookingInfo } = shinkansen;
  const tUrl =
    typeof location.state?.tUrl === 'string'
      ? location.state.tUrl
      : shinkansen.tUrl;
  const bookingId =
    routeBookingId || shinkansen.bookingId || bookingInfo?.bookingId;
  const [loading, setLoading] = useState(Boolean(bookingId));

  useEffect(() => {
    if (token) shinkansen.setToken(token);
    if (routeBookingId) shinkansen.setBookingId(routeBookingId);
    if (tUrl) shinkansen.setTUrl(tUrl);

    if (!bookingId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    void shinkansen
      .getBookingData(bookingId)
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, [bookingId, routeBookingId, shinkansen, tUrl, token]);

  const handleBackHome = () => {
    if (tUrl) {
      window.location.replace(tUrl);
    } else {
      navigate('/');
    }
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <Loading />
      </div>
    );
  }

  // No booking loaded (deep-link without id, or the fetch failed) — show an
  // explicit empty state rather than a misleading "success" layout with blanks.
  if (!bookingInfo) {
    return (
      <div className={styles.container}>
        <p className={styles.empty}>{t('common.noBooking')}</p>
        <div className={styles.footer}>
          <Button
            variant="filled"
            className={styles.homeButton}
            onClick={handleBackHome}
          >
            {t('views.submitted.backHome')}
          </Button>
        </div>
      </div>
    );
  }

  const orderStatus = bookingInfo?.orderStatus ?? OrderStatus.Unreservable;
  const isUnreservable = orderStatus === OrderStatus.Unreservable;
  const isReservePending = orderStatus === OrderStatus.ReservePending;
  const departTime = bookingInfo?.departTime;
  const arriveTime = bookingInfo?.arriveTime;
  const duration = bookingInfo?.timeDuration;
  const trainName = bookingInfo?.trainName;
  const fromStationName = bookingInfo?.fromStationName;
  const toStationName = bookingInfo?.toStationName;
  const departDate = bookingInfo?.departDate;
  const units =
    bookingInfo?.units?.map(unit => `${unit.name} ×${unit.quantity ?? 0}`) ??
    [];
  const trainType = bookingInfo?.trainMode
    ? `${bookingInfo.trainMode}${
        bookingInfo.trainName ? `（${bookingInfo.trainName}）` : ''
      }`
    : '';
  const seatClassName = bookingInfo?.seatClassName;
  const seatPreferenceName = bookingInfo?.seatPreference?.name;

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.intro}>
          <h1 className={styles.title}>{t('views.submitted.title')}</h1>
          <p className={styles.thanks}>{t('views.submitted.thanks')}</p>
          {isUnreservable && (
            <p className={styles.thanksSub}>{t('views.submitted.thanksSub')}</p>
          )}
        </div>

        <section className={cardStyles.card}>
          <div className={cardStyles.trainRow}>
            <div className={cardStyles.trainTime}>
              <span className={cardStyles.time}>{departTime}</span>
              <span className={cardStyles.station}>{fromStationName}</span>
            </div>

            <div className={cardStyles.trainMiddle}>
              <span className={cardStyles.duration}>{duration}</span>
              <div className={cardStyles.line} />
              <span className={cardStyles.trainName}>{trainName}</span>
            </div>

            <div className={cardStyles.trainTime}>
              <span className={cardStyles.time}>{arriveTime}</span>
              <span className={cardStyles.station}>{toStationName}</span>
            </div>
          </div>

          <div className={cardStyles.divider} />

          <div className={cardStyles.details}>
            <h2 className={cardStyles.ticketName}>
              {t('views.submitted.ticketName')}
            </h2>

            {(fromStationName || toStationName) && (
              <div className={cardStyles.detailRow}>
                <span className={cardStyles.label}>
                  {t('views.submitted.route')}
                </span>
                <span className={cardStyles.value}>
                  {fromStationName} → {toStationName}
                </span>
              </div>
            )}

            {departDate && (
              <div className={cardStyles.detailRow}>
                <span className={cardStyles.label}>
                  {t('views.submitted.travelDate')}
                </span>
                <span className={cardStyles.value}>{departDate}</span>
              </div>
            )}

            {units.length > 0 && (
              <div className={cardStyles.detailRow}>
                <span className={cardStyles.label}>
                  {t('views.submitted.ticketCount')}
                </span>
                <span className={cardStyles.value}>{units.join(' ')}</span>
              </div>
            )}

            {trainType && (
              <div className={cardStyles.detailRow}>
                <span className={cardStyles.label}>
                  {t('views.submitted.trainType')}
                </span>
                <span className={cardStyles.value}>{trainType}</span>
              </div>
            )}

            {seatClassName && (
              <div className={cardStyles.detailRow}>
                <span className={cardStyles.label}>
                  {t('views.submitted.seatClass')}
                </span>
                <span className={cardStyles.value}>{seatClassName}</span>
              </div>
            )}

            {seatPreferenceName && (
              <div className={cardStyles.detailRow}>
                <span className={cardStyles.label}>
                  {t('views.submitted.preference')}
                </span>
                <span className={cardStyles.value}>{seatPreferenceName}</span>
              </div>
            )}
          </div>
        </section>

        {isReservePending && (
          <section className={styles.notice}>
            <h2 className={styles.noticeTitle}>
              {t('views.submitted.noticeTitle')}
            </h2>
            <ul className={styles.noticeList}>
              <li>{t('views.submitted.notice1')}</li>
              <li>{t('views.submitted.notice2')}</li>
            </ul>
          </section>
        )}
      </div>

      <div className={styles.footer}>
        <Button
          variant="filled"
          className={styles.homeButton}
          onClick={handleBackHome}
        >
          {t('views.submitted.backHome')}
        </Button>
      </div>
    </div>
  );
};

const Submitted = observer(SubmittedView);

export default Submitted;
